import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import '@/lib/chartjs-register';
import { convertChartOptions } from '@/lib/chartjs-register';
import ChartWidget from '@/components/ui/shared/ChartWidget';
import ColorJson from '../whitelabel.json';

/**
 * MultiAxisBarChart - Functional component replacement for MultiAxisBar_List.js
 * Renders a stacked bar chart with two datasets and list view toggle
 */
function MultiAxisBarChart({ data, startDate, endDate, _Index }) {
    const [chartData, setChartData] = useState([]);
    const [listData, setListData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [legend1, setLegend1] = useState({ text: '', value: '' });
    const [legend2, setLegend2] = useState({ text: '', value: '' });
    const [legend3, setLegend3] = useState({ text: '', value: '' });
    const [sumOfLegendValue, setSumOfLegendValue] = useState('');
    const [noDataMessage, setNoDataMessage] = useState('');
    const [negativeValues, setNegativeValues] = useState(false);
    const abortControllerRef = useRef(null);

    const isRxTheme = useMemo(() => {
        const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        return domain === "rxhacker.com" || 
               domain === "localhost:5464" || 
               window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
               window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
    }, []);

    const themeColors = useMemo(() => {
        if (isRxTheme) {
            return {
                primary: ColorJson.ApplicationColor.Rxhacker_secondary_color,
                hover: ColorJson.ApplicationColor.Rxhacker_extra_color
            };
        }
        return {
            primary: '#2A524B',
            hover: '#FF8B6C'
        };
    }, [isRxTheme]);

    const fetchData = useCallback(async () => {
        if (!data?.url || !data?.RequestJson) return;

        // Cancel any previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsLoading(true);
        setNoDataMessage('Loading. Please wait.');

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1 || '',
                    'X-Browser-Url': window.location.href
                },
                body: data.RequestJson,
                signal: abortControllerRef.current.signal
            });

            const apidata = await response.json();

            if (apidata?.[0]) {
                // Set legends
                if (apidata[0].legend?.length >= 2) {
                    // Transform legend text for specific widgets
                    let legend1Text = apidata[0].legend[0].legendText;
                    let legend2Text = apidata[0].legend[1].legendText;
                    
                    // For Client Access Link Signups widget, map legend text to proper labels
                    if (data?.title === 'Client Access Link Signups') {
                        // If legendText comes as something other than "Desktop"/"Mobile", map it
                        if (legend1Text && legend1Text.toLowerCase().includes('desktop')) {
                            legend1Text = 'Desktop';
                        }
                        if (legend2Text && legend2Text.toLowerCase().includes('mobile')) {
                            legend2Text = 'Mobile';
                        }
                    }
                    
                    setLegend1({
                        text: legend1Text,
                        value: apidata[0].legend[0].legendValue
                    });
                    setLegend2({
                        text: legend2Text,
                        value: apidata[0].legend[1].legendValue
                    });
                }
                if (apidata[0].legend?.length >= 3) {
                    setLegend3({
                        text: apidata[0].legend[2].legendText,
                        value: apidata[0].legend[2].legendValue
                    });
                }

                if (apidata[0].sumOfLegendValues?.[0]) {
                    setSumOfLegendValue(apidata[0].sumOfLegendValues[0].sumOfLegendValue);
                }

                // Set list data
                if (apidata[0].listDataSet && apidata[0].listDataSet.length > 0) {
                    // Extract headers from first row
                    const headers = apidata[0].listDataSet[0]?.datasets?.map(d => d.data) || [];
                    
                    // Extract data rows (skip header)
                    const rows = apidata[0].listDataSet.slice(1).map(row => 
                        row.datasets?.map(d => d.data) || []
                    );
                    
                    // Combine headers and rows
                    setListData([headers, ...rows]);
                }

                // Check for data availability
                const hasData = apidata[0].columnDataSet1?.[0]?.data?.length > 1 || 
                               apidata[0].columnDataSet2?.[0]?.data?.length > 1;
                setNoDataMessage(hasData ? '' : 'No Data Available');

                // Check for negative values
                const hasNegative = apidata[0].columnDataSet1?.[0]?.data?.some(item => item.y < 0);
                setNegativeValues(hasNegative);

                // Build chart data
                if (apidata[0].columnDataSet1?.[0] && apidata[0].columnDataSet2?.[0]) {
                    const dataset1 = apidata[0].columnDataSet1[0];
                    const dataset2 = apidata[0].columnDataSet2[0];

                    setChartData([{
                        labels: dataset1.labels,
                        datasets: [
                            {
                                label: dataset1.toplabel,
                                data: dataset1.data,
                                backgroundColor: themeColors.primary,
                                borderColor: themeColors.primary,
                                borderRadius: 4,
                                stack: '1',
                                hoverBackgroundColor: "#000"
                            },
                            {
                                label: dataset2.toplabel,
                                data: dataset2.data,
                                backgroundColor: data.ColorCode,
                                borderColor: data.ColorCode,
                                borderRadius: 4,
                                stack: '1',
                                hoverBackgroundColor: themeColors.hover
                            }
                        ]
                    }]);
                }
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                setNoDataMessage('Error while loading data');
            }
        } finally {
            setIsLoading(false);
        }
    }, [data?.url, data?.RequestJson, data?.RequestJson1, data?.ColorCode, themeColors]);

    useEffect(() => {
        if ((data?.type === 'MultiBar_List' || data?.type === 'MultiBar_List2') && data?.RequestJson && data?.url) {
            fetchData();
        }
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data?.RequestJson, data?.type, fetchData]);

    const chartOptions = useMemo(() => convertChartOptions({
        layout: { 
            padding: { 
                top: (data?.title === 'Client Access Link Signups' || data?.title === 'New vs Repeat Transactions') ? 10 : 70,
                bottom: 5,
                left: 5,
                right: 5
            } 
        },
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: '#fff',
                titleColor: '#17332e',
                bodyColor: '#17332e',
                borderColor: '#B6ACA2',
                borderWidth: 2,
                padding: 10,
                displayColors: true,
            }
        },
        scales: {
            y: {
                ticks: {
                    color: '#94a3b8',
                    font: { size: 14 },
                    ...(negativeValues && { min: -2, max: 4 })
                },
                grid: { 
                    display: false, 
                    drawBorder: false,
                    drawOnChartArea: false,
                    drawTicks: false
                },
                border: {
                    display: false
                },
                title: {
                    display: !!data?.YlabelString,  // Only show if YlabelString exists
                    text: data?.YlabelString || '',
                    color: '#94a3b8',
                    font: { size: 12 }
                }
            },
            x: {
                offset: true,
                barThickness: 15,
                maxBarThickness: 15,
                ticks: { 
                    display: false,
                    color: '#94a3b8'
                },
                grid: { 
                    display: false, 
                    drawBorder: false,
                    drawOnChartArea: false,
                    drawTicks: false
                },
                border: {
                    display: false
                },
                title: {
                    display: !!data?.XlabelString,  // Only show if XlabelString exists
                    text: data?.XlabelString || '',
                    color: '#94a3b8',
                    font: { size: 12 }
                }
            }
        }
    }), [data?.YlabelString, data?.XlabelString, data?.title, negativeValues]);

    const handleExport = useCallback(() => {
        try {
            const widgetTitle = data?.title || 'export';
            const tableData = [];
            
            // Add all rows (including header)
            listData.forEach((row, rowIndex) => {
                const rowData = row.map((cell, cellIndex) => {
                    let cellValue = String(cell || '').replace(/,/g, '');
                    
                    // Check if this looks like a date in format XX/XX or XX/XX/XXXX
                    // Add a leading space to prevent Excel from auto-formatting dates
                    if (rowIndex > 0 && cellIndex === 0 && /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(cellValue)) {
                        cellValue = ` ${cellValue}`;
                    }
                    
                    return cellValue;
                });
                tableData.push(rowData.join(','));
            });
            
            const csvContent = tableData.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${widgetTitle}.csv`;
            link.href = url;
            document.body.appendChild(link);
            
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (error) {
        }
    }, [listData, data?.title]);

    const chartContent = useMemo(() => {
        if (!chartData.length || !chartData[0]?.labels?.length) {
            return null;
        }
        return (
            <div className="relative h-full flex flex-col">
                {/* Custom Legend - matches production-v3 structure */}
                {(data?.title === 'Client Access Link Signups' || data?.title === 'New vs Repeat Transactions') && (
                    <div className="flex justify-center gap-6 text-sm mb-2 flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: themeColors.primary }}></div>
                            <div className="flex items-center gap-1">
                                <span className="font-bold text-foreground">{legend1.value}</span>
                                <span className="text-muted-foreground">{legend1.text || 'Desktop'}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: data?.ColorCode || '#FF8B6C' }}></div>
                            <div className="flex items-center gap-1">
                                <span className="font-bold text-foreground">{legend2.value}</span>
                                <span className="text-muted-foreground">{legend2.text || 'Mobile'}</span>
                            </div>
                        </div>
                    </div>
                )}
                <div className="flex-1 min-h-0">
                    <Bar data={chartData[0]} options={chartOptions} />
                </div>
            </div>
        );
    }, [chartData, chartOptions, data?.title, data?.ColorCode, legend1, legend2, themeColors]);

    const listHeaders = useMemo(() => {
        if (listData.length > 0 && Array.isArray(listData[0])) {
            // First row is the header
            return listData[0];
        }
        return [];
    }, [listData]);

    const listDataWithoutHeader = useMemo(() => {
        if (listData.length <= 1) return []; // No data rows, only header or empty
        return listData.slice(1); // Skip header row, return full data for ChartWidget to paginate
    }, [listData]);

    // Format legend display
    const displayValue = useMemo(() => {
        if (!sumOfLegendValue) return '';
        
        console.log('MultiAxisBarChart - Title:', data?.title);
        console.log('MultiAxisBarChart - Sum:', sumOfLegendValue);
        console.log('MultiAxisBarChart - Legend1:', legend1);
        console.log('MultiAxisBarChart - Legend2:', legend2);
        console.log('MultiAxisBarChart - Legend3:', legend3);
        
        // For Total Revenue Earned, show formatted breakdown
        if (data?.title === 'Total Revenue Earned') {
            const formatCurrency = (value) => {
                if (!value || value === '0' || value === 'NaN') return '$ 0.00';
                const numValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
                if (isNaN(numValue)) return '$ 0.00';
                return `$ ${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            };
            
            console.log('Returning JSX breakdown for Total Revenue Earned');
            
            return (
                <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">
                        {formatCurrency(sumOfLegendValue)}
                    </div>
                    <div className="space-y-1 text-sm">
                        <div>
                            <div className="text-primary font-medium">{legend1.text || 'Direct'}</div>
                            <div className="text-muted-foreground">{formatCurrency(legend1.value)}</div>
                        </div>
                        <div>
                            <div className="text-primary font-medium">{legend2.text || 'Override'}</div>
                            <div className="text-muted-foreground">{formatCurrency(legend2.value)}</div>
                        </div>
                        {legend3.text && (
                            <div>
                                <div className="text-primary font-medium">{legend3.text || 'Bonus'}</div>
                                <div className="text-muted-foreground">{formatCurrency(legend3.value)}</div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        
        // For Client Access Link Signups, show just the total inline
        if (data?.title === 'Client Access Link Signups') {
            return sumOfLegendValue;
        }
        
        if (data?.title === 'New vs Repeat Transactions') {
            return sumOfLegendValue;
        }
        return `$ ${sumOfLegendValue}`;
    }, [sumOfLegendValue, data?.title, legend1, legend2, legend3]);

    return (
        <ChartWidget
            title={data?.title}
            subtitle={data?.Subtitle}
            legendValue={displayValue}
            isLoading={isLoading}
            onExport={handleExport}
            noDataMessage={noDataMessage}
            hasData={chartData.length > 0 && chartData[0]?.labels?.length > 0}
            chartContent={chartContent}
            listHeaders={listHeaders}
            listData={listDataWithoutHeader}
            showToggle={true}
            defaultView="chart"
            chartType="Bar"
            minHeight="300px"
        />
    );
}

export default MultiAxisBarChart;

